import {
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  FormControlLabel,
  IconButton,
  MenuItem,
  Select,
  Switch,
  Tooltip,
} from '@mui/material';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { AgGridReact } from 'ag-grid-react';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';

import ArchiveIcon from '@mui/icons-material/Archive';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import ShieldIcon from '@mui/icons-material/Shield';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import UnarchiveIcon from '@mui/icons-material/Unarchive';
import moment from 'moment';
import { OfferContext } from '../context';
import OfferFilter from '../OfferFilter';
import useOffer from '../useOffer';
import OfferTableToolbar from './Toolbar';

import { Link } from 'react-router-dom';

import ContentCopyIcon from '@mui/icons-material/ContentCopy';

const emptyFilter = {
  dateRange: [{ startDate: null, endDate: null, key: 'selection' }],
  name: '',
  offerLink: '',
  offerId: '',
  status: null,
  redirectDomain: null,
  entryDomain: null,
  trafficSource: null,
};

const buildFilterModel = (filter) => {
  const model = {};
  if (filter.name) model.name = { type: 'contains', filter: filter.name };
  if (filter.offerLink) model.offerLink = { type: 'contains', filter: filter.offerLink };
  if (filter.offerId) model.uniqueId = { type: 'contains', filter: filter.offerId };
  if (filter.status !== null && filter.status !== undefined) {
    model.status = { type: 'equals', filter: filter.status };
  }
  if (filter.redirectDomain?.value) {
    if (filter.redirectDomain.mode === 'is') {
      model.redirectLink = { type: 'contains', filter: filter.redirectDomain.value.domainLink };
    } else {
      model.redirectDomainLink = { type: 'contains', filter: filter.redirectDomain.value };
    }
  }
  if (filter.entryDomain?.value) {
    if (filter.entryDomain.mode === 'is') {
      model.entryDomain = { type: 'contains', filter: filter.entryDomain.value.domainLink };
    } else {
      model.entryDomainLink = { type: 'contains', filter: filter.entryDomain.value };
    }
  }
  if (filter.trafficSource?.value) {
    if (filter.trafficSource.mode === 'is') {
      model.trafficSource = { type: 'equals', filter: filter.trafficSource.value.name };
    } else {
      model.trafficSource = { type: 'contains', filter: filter.trafficSource.value };
    }
  }
  return model;
};

function OfferTable() {
  const { getOffers, editOffer, specificEditOffer } = useOffer();
  const [offers, setOffers] = useState([]);
  const [selectedRowData, setSelectedRowData] = useState(undefined);
  const [multipleDataSelected, setMultipleDataSelected] = useState(false);
  const [filteredOffers, setFilteredOffers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedRow, setSelectedRow] = useState('');
  const [selectedRows, setSelectedRows] = useState([]);
  const [offerFilter, setOfferFilter] = useState(emptyFilter);
  const [showArchive, setShowArchive] = useState(false);
  const [finalOffers, setFinalOffers] = useState([]);
  const gridApi = useRef(null);
  const [pagination, setPagination] = useState({ page: 0, pageSize: 20 });
  const [totalRowCount, setTotalRowCount] = useState(0);
  const [sortModel, setSortModel] = useState({
    sortColumn: null,
    sort: null,
  });

  const {
    traffic,
    loading: contextLoading,
    domains,
  } = useContext(OfferContext);

  const handleShowArchiveChange = (event) => {
    setShowArchive(event.target.checked);
  };

  const domainMap = useMemo(() => {
    return domains.reduce((acc, item) => {
      acc[item._id] = item;
      return acc;
    }, {});
  }, [domains]);

  const trafficMap = useMemo(() => {
    return traffic.reduce((acc, item) => {
      acc[item._id] = item;
      return acc;
    }, {});
  }, [traffic]);

  const init = (page = 1, limit = 20, sortColumn = null, sort = null, filterModel = {}, startDate = null, endDate = null) => {
    const apiCall = getOffers();
    (async function () {
      try {
        setLoading(true);
        const res = await apiCall.call(page, limit, sortColumn, sort, filterModel, startDate, endDate);
        formatOfferData(res.offers);
        setTotalRowCount(res.totalCount);
        setLoading(false);
      } catch (e) {
        console.error('Error fetching offers:', e);
        setLoading(false);
      }
    })();

    return apiCall.abort;
  };

  const onEventDone = () => {
    const filterModel = buildFilterModel(offerFilter);
    const { dateRange } = offerFilter;
    const startDate = dateRange[0].startDate ? new Date(dateRange[0].startDate).toISOString() : null;
    const endDate = dateRange[0].endDate ? new Date(dateRange[0].endDate).toISOString() : null;
    init(
      pagination.page + 1,
      pagination.pageSize,
      sortModel.sortColumn,
      sortModel.sort,
      filterModel,
      startDate,
      endDate,
    );
  };

  const handleFilterApply = (newFilter) => {
    setPagination((prev) => ({ ...prev, page: 0 }));
    setOfferFilter(newFilter);
  };

  useEffect(() => {
    if (contextLoading) return;

    const filterModel = buildFilterModel(offerFilter);
    const { dateRange } = offerFilter;
    const startDate = dateRange[0].startDate ? new Date(dateRange[0].startDate).toISOString() : null;
    const endDate = dateRange[0].endDate ? new Date(dateRange[0].endDate).toISOString() : null;

    init(
      pagination.page + 1,
      pagination.pageSize,
      sortModel.sortColumn,
      sortModel.sort,
      filterModel,
      startDate,
      endDate,
    );
  }, [offerFilter, pagination.page, pagination.pageSize, sortModel, contextLoading]);

  const updateOfferArchiveStatus = (id, newArchiveStatus) => {
    const newArchiveStatusItem = { archive: newArchiveStatus };
    const updatedOffers = offers.map((item) => {
      if (item._id === id) {
        const updatedOffer = { ...item, archive: newArchiveStatus };
        specificEditOffer().call(id, newArchiveStatusItem);
        return updatedOffer;
      }
      return item;
    });
    setOffers(updatedOffers);
  };


  const getRedirectLink = (offer) => {
    const { redirectDomainId = '' } = offer

    const domainData = domainMap[redirectDomainId];

    if (!domainData) {
      return ''
    }

    const domainName = domainData.domainName ?? ''

    const isOnlyDrops = domainName.includes('onlydrops')

    try {
      const domainObject = new URL(domainData.domainLink);

      if (isOnlyDrops) {
        domainObject.searchParams.set('uid', offer.uniqueId)
        return domainObject.toString()
      }

      const url = `${domainObject.origin}/r/${offer.uniqueId}`

      return url

    } catch (error) {
      return ''
    }
  }

  const getEntryDomainLink = (offer) => {
    const { entryDomainId = '' } = offer

    const entryDomainData = domainMap[entryDomainId];

    if (!entryDomainData) return ''

    try {
      const url = new URL(entryDomainData.domainLink)

      const fullUrl = `${url.origin}/offer/${offer.uniqueId}`

      return fullUrl
    } catch (error) {
      return ''
    }
  }


  const renderStatusIcon = (Icon, isActive, title) => (
    <Tooltip title={title}>
      <Icon
        sx={{
          flexShrink: 0,
          verticalAlign: 'middle',
          fontSize: 18,
          mr: 1,
          color: isActive ? '#4CAF50' : '#F44336',
        }}
      />
    </Tooltip>
  );

  const renderCell = (type, data) => {
    switch (type) {
      case 'name':
        const isOfferActive = data.status ?? true;
        const isArchived = Boolean(data.archive);

        return (
          <>
            {renderStatusIcon(PowerSettingsNewIcon, isOfferActive, isOfferActive ? 'Active' : 'Inactive')}
            {renderStatusIcon(ArchiveIcon, isArchived, isArchived ? 'Archived' : 'Not archived')}
            {data[type]}
          </>
        );
      case 'offerLink':
        return data[type];
      case 'fallBackUrl':
        return data[type];  
      case 'redirectLink':
        return renderCopyableLink(data[type]);

      case 'entryDomain':
        return renderCopyableLink(data[type]);
      case 'ironDomeFallback':
        const isIronDomeActive = Boolean(data.ironDomeActive);

        const ironDomeIcon = (
          <Link
            to={`/iron-dome?offerId=${data._id}`}
            style={{ display: 'inline-flex', flexShrink: 0 }}
          >
            {renderStatusIcon(
              ShieldIcon,
              isIronDomeActive,
              `Iron Dome rule ${isIronDomeActive ? 'active' : 'inactive'} (click to edit)`,
            )}
          </Link>
        );

        if (!data[type]) return ironDomeIcon;

        return renderCopyableLink(data[type], ironDomeIcon);
      case 'trafficSource':
        const trafficSourceId = data.trafficSourceId || '';

        if (!trafficSourceId)
          return <Chip label="No traffic source" color="warning" />;

        const label = data[type];

        if (!label) return <Chip label="No traffic source" color="warning" />;

        return (
          <Link
            to={{
              pathname: `/traffic/${trafficSourceId}`,
              state: { trafficSourceId },
            }}
          >
            <Chip label={label} color="info" sx={{ cursor: 'pointer' }} />
          </Link>
        );
      case '_id':
        const clipboardText = getRedirectLink(data)

        const handleArchiveToggle = () => {
          const newArchiveStatus = !data.archive;
          updateOfferArchiveStatus(data._id, newArchiveStatus);
        };

        return (
          <Box>
            <Tooltip title="Open redirect link">
              <a href={clipboardText} target=" _blank" rel="noreferrer noopenner">
                <IconButton>
                  <OpenInNewIcon />
                </IconButton>
              </a>
            </Tooltip>
            <Tooltip title={data.archive ? 'Unarchive' : 'Archive'}>
              <IconButton onClick={handleArchiveToggle}>
                {data.archive ? <UnarchiveIcon /> : <ArchiveIcon />}
              </IconButton>
            </Tooltip>
          </Box>
        );

      default:
        return data[type];
    }
  };

  const formatOfferData = (offers, usingFunction = 'default') => {

    offers.forEach((offer, index) => {
      const redirectLink = getRedirectLink(offer)
      offers[index].redirectLink = redirectLink;

      const entryDomainLink = getEntryDomainLink(offer)
      offers[index].entryDomain = entryDomainLink;

      const trafficSourceId = offer.trafficSourceId || '';
      const trafficSourceData = trafficMap[trafficSourceId];
      offers[index].trafficSource = trafficSourceData ? trafficSourceData.name : '';

      const redirectDomainData = domainMap[offer.redirectDomainId];
      offers[index].redirectDomainName = redirectDomainData ? (redirectDomainData.domainName || '') : '';

      const entryDomainData = domainMap[offer.entryDomainId];
      offers[index].entryDomainName = entryDomainData ? (entryDomainData.domainName || '') : '';
    });

    if (usingFunction !== 'default') {
      setFilteredOffers(offers);
    } else {
      setOffers(offers);
    }
  };

  const gettingFinalOffers = () => {
    let finalOffer;
    if (showArchive) {
      finalOffer = offers;
    } else {
      finalOffer = offers.filter((offer) => offer.archive === false || offer.archive === undefined);
    }
    setFinalOffers(finalOffer);
  };
  useEffect(() => {
    gettingFinalOffers();
  }, [showArchive, offers]);

  const getTrafficSourceId = (val) => {
    if (typeof val !== 'string') return '';
    const trimmedVal = val.trim();
    for (const trafficSourceId in trafficMap) {
      if (trafficMap.hasOwnProperty(trafficSourceId)) {
        const trafficSource = trafficMap[trafficSourceId];
        if (trafficSource?.name?.trim() === trimmedVal) {
          return trafficSourceId;
        }
      }
    }
    return '';
  };
  const getRedirectDomainId = (val) => {
    let trimmedVal;

    if (val.includes('/r/')) {
      trimmedVal = val.split('/r/')[0];
    } else if (val.includes('/?uid=')) {
      trimmedVal = val.split('/?uid=')[0];
    } else {
      trimmedVal = val;
    }

    for (const domainId in domainMap) {
      if (domainMap.hasOwnProperty(domainId)) {
        const domain = domainMap[domainId];
        if (domain.domainLink === trimmedVal) {
          return domainId;
        }
      }
    }

    return '';
  };

  const getEntryDomainId = (val) => {
    for (const entryId in domainMap) {
      if (domainMap.hasOwnProperty(entryId)) {
        const entry = domainMap[entryId];
        if (entry.domainLink === val) {
          return entryId;
        }
      }
    }

    return '';
  };

  const handlePageSizeChange = (event) => {
    setPagination((prev) => ({
      ...prev,
      pageSize: Number(event.target.value),
      page: 0,
    }));
  };

  const handlePageChange = (newPage) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
  };

  const onSortChanged = () => {
    if (gridApi.current) {
      const sortState = gridApi.current
        .getColumnState()
        .filter((col) => col.sort !== null && col.sort !== undefined);
      if (sortState.length > 0) {
        setSortModel({
          sortColumn: sortState[0].colId,
          sort: sortState[0].sort,
        });
      } else {
        setSortModel({ sortColumn: null, sort: null });
      }
    }
  };

  useEffect(() => {
    if (selectedRows.length > 1) {
      setSelectedRowData(undefined);
    } else {
      setSelectedRowData(offers.find((item) => item._id === selectedRows[0]));
    }
  }, [selectedRows]);

  useEffect(() => {
    setSelectedRows([]);
  }, [offers]);

  const generateTableCols = () => {
    return [
      {
        field: 'name',
        headerName: 'Name',
        headerTooltip: 'Power icon: offer active/inactive. Archive icon: archived/not archived',
        checkboxSelection: true,
        headerCheckboxSelection: true,
        headerCheckboxSelectionFilteredOnly: true,
        cellRenderer: (params) => renderCell('name', params.data),
      },
      {
        field: 'offerLink',
        headerName: 'Original Offer Link',
        cellRenderer: (params) => renderCell('offerLink', params.data),
      },
      {
        field: 'fallBackUrl',
        headerName: 'Fallback URL',   
        cellRenderer: (params) => renderCell('fallBackUrl', params.data),
      },
      {
        field: 'redirectLink',
        headerName: 'Redirect Link',
        cellRenderer: (params) => renderCell('redirectLink', params.data),
        cellStyle: {
          overflow: 'visible',
        },
      },
      {
        field: 'entryDomain',
        headerName: 'Entry Domain',
        cellRenderer: (params) => renderCell('entryDomain', params.data),
        cellStyle: {
          overflow: 'visible',
        },
      },
      {
        field: 'trafficSource',
        headerName: 'Traffic Source',
        cellRenderer: (params) => renderCell('trafficSource', params.data),
        valueGetter: (params) => params.data.trafficSource,
      },
      {
        field: 'createdAt',
        headerName: 'Created At',
        cellRenderer: (params) => {
          if (!params.value) return '';
          return moment.utc(params.value).format('YYYY-MM-DD HH:mm') + ' UTC';
        },
      },
      {
        field: 'updatedAt',
        headerName: 'Updated At',
        cellRenderer: (params) => {
          if (!params.value) return '';
          return moment.utc(params.value).format('YYYY-MM-DD HH:mm') + ' UTC';
        },
      },
      {
        field: 'ironDomeFallback',
        headerName: 'Iron Dome Fallback URL',
        headerTooltip: 'Shield icon shows whether the Iron Dome rule is active',
        sortable: false,
        cellRenderer: (params) => renderCell('ironDomeFallback', params.data),
        cellStyle: (params) =>
          params.data.ironDomeFallback
            ? { overflow: 'visible' }
            : { display: 'flex', alignItems: 'center' },
      },
      {
        field: '_id',
        headerName: 'Actions',
        sortable: false,
        cellRenderer: (params) => renderCell('_id', params.data),
      },
    ];
  };

  const tableCols = generateTableCols();

  const renderCopyableLink = (value, prefix = null) => {
      if (!value) {
        return <Chip color="error" label="Invalid URL" />;
      }

      return (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            width: '100%',
            minWidth: 0,
            gap: 0.5,
          }}
        >
        {prefix}
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            overflowX: 'auto',
            overflowY: 'hidden',
            whiteSpace: 'nowrap',

            '&::-webkit-scrollbar': {
              height: '4px',
            },
            '&::-webkit-scrollbar-thumb': {
              borderRadius: '4px',
            },
          }}
        >
          {value}
        </Box>

        <Tooltip title="Copy link">
          <IconButton
            size="small"
            onClick={() => copyToClipboard(value)}
            sx={{
              flexShrink: 0,
            }}
          >
            <ContentCopyIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    );
  };

  return (
    <Card>
      {/* Active filter chips */}
      <Box display="flex" gap={1} flexWrap="wrap" alignItems="center" px={1} pt={1}>
        {offerFilter.dateRange[0].startDate && offerFilter.dateRange[0].endDate && (
          <Chip
            size="small"
            label={`${new Date(offerFilter.dateRange[0].startDate).toLocaleDateString()} → ${new Date(offerFilter.dateRange[0].endDate).toLocaleDateString()}`}
            onDelete={() => handleFilterApply({ ...offerFilter, dateRange: [{ startDate: null, endDate: null, key: 'selection' }] })}
          />
        )}
        {offerFilter.name && (
          <Chip
            size="small"
            label={`Name: ${offerFilter.name}`}
            onDelete={() => handleFilterApply({ ...offerFilter, name: '' })}
          />
        )}
        {offerFilter.offerLink && (
          <Chip
            size="small"
            label={`Offer Link: ${offerFilter.offerLink}`}
            onDelete={() => handleFilterApply({ ...offerFilter, offerLink: '' })}
          />
        )}
        {offerFilter.offerId && (
          <Chip
            size="small"
            label={`Offer ID: ${offerFilter.offerId}`}
            onDelete={() => handleFilterApply({ ...offerFilter, offerId: '' })}
          />
        )}
        {offerFilter.status !== null && offerFilter.status !== undefined && (
          <Chip
            size="small"
            label={`Status: ${offerFilter.status === 'active' ? 'Active' : 'Inactive'}`}
            onDelete={() => handleFilterApply({ ...offerFilter, status: null })}
          />
        )}
        {offerFilter.redirectDomain?.value && (
          <Chip
            size="small"
            label={`Redirect Domain ${offerFilter.redirectDomain.mode === 'is' ? 'Is' : 'Contains'}: ${
              offerFilter.redirectDomain.mode === 'is'
                ? (offerFilter.redirectDomain.value.domainName || offerFilter.redirectDomain.value.domainLink)
                : offerFilter.redirectDomain.value
            }`}
            onDelete={() => handleFilterApply({ ...offerFilter, redirectDomain: null })}
          />
        )}
        {offerFilter.entryDomain?.value && (
          <Chip
            size="small"
            label={`Entry Domain ${offerFilter.entryDomain.mode === 'is' ? 'Is' : 'Contains'}: ${
              offerFilter.entryDomain.mode === 'is'
                ? (offerFilter.entryDomain.value.domainName || offerFilter.entryDomain.value.domainLink)
                : offerFilter.entryDomain.value
            }`}
            onDelete={() => handleFilterApply({ ...offerFilter, entryDomain: null })}
          />
        )}
        {offerFilter.trafficSource?.value && (
          <Chip
            size="small"
            label={`Traffic Source ${offerFilter.trafficSource.mode === 'is' ? 'Is' : 'Contains'}: ${
              offerFilter.trafficSource.mode === 'is'
                ? offerFilter.trafficSource.value.name
                : offerFilter.trafficSource.value
            }`}
            onDelete={() => handleFilterApply({ ...offerFilter, trafficSource: null })}
          />
        )}
      </Box>

      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Box display="flex" alignItems="center">
          <OfferTableToolbar
            onAddOfferDone={onEventDone}
            onEditOfferDone={onEventDone}
            onCloneOffer={onEventDone}
            selectedRow={selectedRowData}
            multipleDataSelected={multipleDataSelected}
            filteredOffers={filteredOffers}
            onImportOfferDone={onEventDone}
            onDeleteOfferDone={onEventDone}
            onBulkArchiveDone={onEventDone}
            onEditBulkOfferDone={onEventDone}
            rowToDelete={selectedRows}
            rowToArchive={selectedRows}
            currentData={offers}
            getTrafficSourceId={getTrafficSourceId}
            getRedirectDomainId={getRedirectDomainId}
            getEntryDomainId={getEntryDomainId}
            showArchive={showArchive}
            domainMap={domainMap}
            trafficMap={trafficMap}
            setLoading={setLoading}
            fetchData={onEventDone}
            totalRowCount={totalRowCount}
          />
          <OfferFilter
            domains={domains}
            traffic={traffic}
            onApply={handleFilterApply}
            appliedFilter={offerFilter}
          />
        </Box>
        <FormControlLabel
          control={
            <Switch
              checked={showArchive}
              onChange={handleShowArchiveChange}
              name="showArchived"
              color="primary"
            />
          }
          label="Show Archived"
        />
      </Box>
      <Box
        sx={{
          width: '100%',
          height: '1000px',
        }}
        className="ag-theme-alpine"
      >
        <Box position="relative" height="100%">
          <AgGridReact
            rowData={finalOffers}
            columnDefs={tableCols}
            defaultColDef={{
              sortable: true,
              filter: false,
              resizable: true,
              minWidth: 100,
              wrapHeaderText: true,
              autoHeaderHeight: true,
            }}
            autoSizeStrategy={{ type: 'fitGridWidth' }}
            onGridSizeChanged={(params) => params.api.sizeColumnsToFit()}
            enableCellTextSelection={true}
            rowSelection="multiple"
            onSortChanged={onSortChanged}
            pagination={false}
            onGridReady={(params) => {
              gridApi.current = params.api;
            }}
            onSelectionChanged={(event) => {
              const selectedNodes = event.api.getSelectedNodes();
              const selectedData = selectedNodes.map((node) => node.data);
              setSelectedRows(selectedData.map((data) => data._id));

              const newSelectedData =
                selectedData.length === 1 ? selectedData[0] : undefined;

              if (selectedData.length > 1) {
                setMultipleDataSelected(true)
              } else {
                setMultipleDataSelected(false)
              }

              formatOfferData(selectedData, 'agGrid');
              setSelectedRowData(newSelectedData);
            }}
          />
          {loading && (
            <Box
              position="absolute"
              top="0"
              left="0"
              right="0"
              bottom="0"
              display="flex"
              justifyContent="center"
              alignItems="center"
              bgcolor="rgba(255, 255, 255, 0.7)"
              zIndex={1}
            >
              <CircularProgress />
            </Box>
          )}
        </Box>
      </Box>
      <Box display="flex" justifyContent="flex-end" alignItems="center" mt={2}>
        <Box display="flex" alignItems="center" ml={2}>
          <Box component="span" sx={{ mr: 1 }}>
            Page {pagination.page + 1} of{' '}
            {Math.ceil(totalRowCount / pagination.pageSize)} | Total offers: {totalRowCount}
          </Box>
          <Select
            value={pagination.pageSize}
            onChange={handlePageSizeChange}
            size="small"
            sx={{ mr: 2 }}
          >
            {[10, 20, 30, 50, 100, 200].map((size) => (
              <MenuItem key={size} value={size}>
                {size} per page
              </MenuItem>
            ))}
          </Select>
        </Box>
        <Button
          variant="contained"
          color="primary"
          onClick={() => handlePageChange(Math.max(0, pagination.page - 1))}
          disabled={pagination.page === 0}
        >
          Previous
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={() => handlePageChange(pagination.page + 1)}
          disabled={(pagination.page + 1) * pagination.pageSize >= totalRowCount}
          sx={{ ml: 2 }}
        >
          Next
        </Button>
      </Box>
    </Card>
  );
}

const copyToClipboard = (text) => {
  navigator.clipboard.writeText(text);
};

export default OfferTable;
